import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAchievements, getPlayer } from '../api';
import PageHeader from '../components/PageHeader';
import MatchList from '../components/MatchList';

export default function Player() {
  const { name = '' } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ['player', name],
    queryFn: () => getPlayer(name),
    enabled: !!name,
  });

  const { data: ach } = useQuery({ queryKey: ['ach', name], queryFn: () => getAchievements(decodeURIComponent(name)), enabled: !!name });

  if (isLoading) return <PageHeader title={decodeURIComponent(name)} />;
  if (error) return <ErrorBox msg={(error as Error).message} />;
  if (!data) return null;

  const myCodes = data.player.teams.map((t) => t.code);
  const upcoming = data.matches.filter((m) => m.status !== 'post');
  const completed = data.matches.filter((m) => m.status === 'post');

  return (
    <>
      <PageHeader
        title={data.player.name}
        subtitle={
          <Link to="/leaderboard" className="text-pitch-400 hover:underline">← Standings</Link>
        }
        right={
          <div className="text-right grid grid-cols-2 gap-x-4 items-end">
            <div>
              <div className="stat text-3xl font-semibold">{data.standing?.points ?? 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-dim">now</div>
            </div>
            <div>
              <div className="stat text-3xl font-semibold text-pitch-400">
                {(data.standing?.projectedPoints ?? 0).toFixed(1)}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-dim">projected</div>
            </div>
          </div>
        }
      />

      <section className="px-4 lg:px-0 mb-6">
        <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Teams</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {data.player.teams.map((t) => {
            const tp = data.standing?.teams.find((x) => x.code === t.code);
            return (
              <Link key={t.code} to={`/teams/${t.code}`} className="card p-3 hover:border-pitch-500/40 transition-colors">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl leading-none">{t.flag}</span>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold truncate">{t.name}</div>
                    <div className="text-[10px] text-dim">{t.code}</div>
                  </div>
                </div>
                <div className="flex items-baseline justify-between text-xs">
                  <span className="text-dim">{tp?.played ?? 0} GP</span>
                  <span className="stat text-pitch-400 text-base">
                    {tp?.points ?? 0}
                    {tp && tp.expectedRemaining > 0 && (
                      <span className="text-dim text-xs"> +{tp.expectedRemaining.toFixed(1)}</span>
                    )}
                  </span>
                </div>
                {tp && tp.played > 0 && (
                  <div className="text-[10px] text-dim mt-1">
                    {tp.wins}W · {tp.draws}D · {tp.losses}L
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </section>

      <section className="px-4 lg:px-0 mb-6">
        <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Upcoming</h2>
        <MatchList matches={upcoming} highlightCodes={myCodes} showOwners emptyText="No upcoming matches." />
      </section>

      <section className="px-4 lg:px-0">
        <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Completed</h2>
        <MatchList matches={completed} highlightCodes={myCodes} showOwners emptyText="No completed matches yet." />
      </section>

      {ach && (ach.achievements.length > 0 || ach.rivalries.length > 0) && (
        <>
          <section className="px-4 lg:px-0 mt-6">
            <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Achievements</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {ach.achievements.map((a) => (
                <div key={a.id} className={`card p-3 ${a.earned ? '' : 'opacity-40'}`}>
                  <div className="text-xs font-semibold">{a.title}</div>
                  <div className="text-[10px] text-dim mt-0.5">{a.body}</div>
                  {a.earned && a.earnedAt && (
                    <div className="text-[10px] text-pitch-400 mt-1">Earned {new Date(a.earnedAt).toLocaleDateString()}</div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {ach.rivalries.length > 0 && (
            <section className="px-4 lg:px-0 mt-6">
              <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Rivalries</h2>
              <div className="card overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-dim border-b border-ink-800">
                      <th className="text-left py-2 px-3 font-medium">vs</th>
                      <th className="font-medium text-center">Bets W-L</th>
                      <th className="font-medium text-center">Net €</th>
                      <th className="font-medium text-center pr-3">H2H teams</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ach.rivalries.map((r) => (
                      <tr key={r.opponent} className="border-b border-ink-800/40 last:border-0">
                        <td className="py-2 px-3 font-semibold">{r.opponent}</td>
                        <td className="text-center stat">{r.bets.wins}-{r.bets.losses}</td>
                        <td className={`text-center stat ${r.bets.net > 0 ? 'text-pitch-400' : r.bets.net < 0 ? 'text-red-400' : 'text-dim'}`}>
                          {r.bets.net > 0 ? '+' : ''}€{r.bets.net}
                        </td>
                        <td className="text-center stat pr-3">{r.fantasy.mineWins}-{r.fantasy.theirWins}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}

function ErrorBox({ msg }: { msg: string }) {
  return <div className="px-4 lg:px-0 card p-6 text-sm text-red-300">Failed to load: {msg}</div>;
}
